import type { CostLineItem, CostSection } from "./types";

interface RawCostItem {
  category: string | null;
  priority: string | null;
  description: string;
  cost: number | string | null;
  sort_order: number | null;
  run_id: string;
}

export function computeCostSections(
  costItems: RawCostItem[],
  hvacDeficit: number | null,
  oldestHvacUnitAgeYears: number | null
): { costSections: CostSection[]; totalReplacementCost: number } {
  if (costItems.length === 0) {
    return { costSections: [], totalReplacementCost: 0 };
  }

  // Get the latest run_id
  const latestRunId = costItems[0].run_id;

  // Filter to only the latest run's items, then sort by sort_order
  const items = latestRunId
    ? costItems
        .filter((item) => item.run_id === latestRunId)
        .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999))
    : costItems;

  // Group by category, preserving sort order
  const grouped = new Map<string, { priority: "HIGH" | "MED" | "LOW"; lineItems: CostLineItem[] }>();

  for (const item of items) {
    const category = item.category ?? "Other";
    const priority = (["HIGH", "MED", "LOW"].includes(item.priority ?? "")
      ? item.priority
      : "LOW") as "HIGH" | "MED" | "LOW";
    const cost = typeof item.cost === "number" ? item.cost : Number(item.cost) || 0;

    const lineItem: CostLineItem = {
      assetId: "",
      description: item.description,
      cost,
    };

    const existing = grouped.get(category);
    if (existing) {
      existing.lineItems.push(lineItem);
      const pOrder = { HIGH: 0, MED: 1, LOW: 2 };
      if (pOrder[priority] < pOrder[existing.priority]) {
        existing.priority = priority;
      }
    } else {
      grouped.set(category, { priority, lineItems: [lineItem] });
    }
  }

  // Convert to sorted sections
  let costSections: CostSection[] = Array.from(grouped.entries())
    .map(([category, { priority, lineItems }]) => ({
      category,
      priority,
      totalCost: lineItems.reduce((sum, li) => sum + (li.cost ?? 0), 0),
      lineItems,
    }))
    .sort((a, b) => b.totalCost - a.totalCost);

  // If HVAC tonnage installed >= min required AND oldest unit is under 10 years,
  // remove HVAC replacement from cost sections
  const hvacUnitsAreSufficient = hvacDeficit !== null && hvacDeficit >= 0;
  const oldestUnitUnder10 = oldestHvacUnitAgeYears !== null && oldestHvacUnitAgeYears < 10;
  if (hvacUnitsAreSufficient && oldestUnitUnder10) {
    const hvacKeywords = ["hvac", "air condition", "a/c", "ac unit", "cooling", "heating"];
    costSections = costSections.filter((section) => {
      const catLower = section.category.toLowerCase();
      return !hvacKeywords.some((kw) => catLower.includes(kw));
    });
  }

  const totalReplacementCost = costSections.reduce((sum, s) => sum + s.totalCost, 0);
  return { costSections, totalReplacementCost };
}
